import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { FaSearch } from "react-icons/fa";
import { RootState } from "../Services/Store";
import WebService from "../Services/WebService";
import styles from "./DailyWorksheet.module.css";

function localDateStr(d = new Date()) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

const DailyWorksheet: React.FC = () => {
  const navigate = useNavigate();
  const userType = useSelector((state: RootState) => state.auth.user?.user_type)?.toUpperCase?.() || "";
  const isAdmin = userType === "ADMIN";
  const [reportDate, setReportDate] = useState(localDateStr());
  const [search, setSearch] = useState("");
  const [report, setReport] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isAdmin || !reportDate) return;
    setLoading(true);
    setError(null);
    WebService.getAPI({
      action: `/v1/door-management/tasks/daily-worksheet?date=${encodeURIComponent(reportDate)}`,
    })
      .then((res: any) => setReport(res?.data ?? null))
      .catch(() => setError("Failed to load daily worksheet"))
      .finally(() => setLoading(false));
  }, [isAdmin, reportDate]);

  if (!isAdmin) {
    return (
      <div className={styles.pageWrapper}>
        <div className={styles.denied}>Access Denied: Daily Worksheet is available to admin only.</div>
      </div>
    );
  }

  const employees = ((report?.employees || []) as any[]).filter((row) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return String(row.employee_name || "").toLowerCase().includes(q);
  });
  const totals = report?.totals;

  return (
    <div className={styles.pageWrapper}>
      <h2 className={styles.title}>Daily Worksheet</h2>
      <div className={styles.filterRow}>
        <div className={styles.filterLeft}>
          <div className={styles.inputGroup}>
            <FaSearch className={styles.inputIcon} />
            <input
              className={styles.input}
              type="text"
              placeholder="Search employee..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className={styles.inputGroup}>
            <input
              className={styles.input}
              type="date"
              value={reportDate}
              max={localDateStr()}
              onChange={(e) => setReportDate(e.target.value)}
            />
          </div>
        </div>
      </div>

      {totals && (
        <div className={styles.statsRow}>
          <div className={styles.statCard}>
            <strong>{totals.total_assigned ?? 0}</strong>
            <span>Assigned</span>
          </div>
          <div className={styles.statCard}>
            <strong>{totals.total_completed ?? 0}</strong>
            <span>Completed</span>
          </div>
          <div className={styles.statCard}>
            <strong>{totals.total_in_progress ?? 0}</strong>
            <span>In Progress</span>
          </div>
          <div className={styles.statCard}>
            <strong>{totals.total_pending ?? 0}</strong>
            <span>Pending</span>
          </div>
          <div className={styles.statCard}>
            <strong>{totals.total_due ?? 0}</strong>
            <span>Due</span>
          </div>
          <div className={`${styles.statCard} ${styles.statOverdue}`}>
            <strong>{totals.total_overdue ?? 0}</strong>
            <span>Overdue</span>
          </div>
          <div className={styles.statCard}>
            <strong>{totals.completion_rate ?? 0}%</strong>
            <span>Completion</span>
          </div>
        </div>
      )}

      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Employee</th>
              <th>Assigned</th>
              <th>Completed</th>
              <th>In Progress</th>
              <th>Pending</th>
              <th>Due</th>
              <th>Overdue</th>
              <th>Performance</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={8} className={styles.centerCell}>Loading...</td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan={8} className={styles.errorCell}>{error}</td>
              </tr>
            ) : employees.length === 0 ? (
              <tr>
                <td colSpan={8} className={styles.centerCell}>No employees found</td>
              </tr>
            ) : (
              employees.map((row) => (
                <tr key={row.user_id}>
                  <td>
                    <span
                      className={styles.nameLink}
                      onClick={() => row.user_id && navigate(`/task-management/${row.user_id}`)}
                    >
                      {row.employee_name || "—"}
                    </span>
                  </td>
                  <td>{row.total_assigned ?? 0}</td>
                  <td>{row.total_completed ?? 0}</td>
                  <td>{row.total_in_progress ?? 0}</td>
                  <td>{row.total_pending ?? 0}</td>
                  <td>{row.total_due ?? 0}</td>
                  <td className={row.total_overdue > 0 ? styles.overdueCell : undefined}>
                    {row.total_overdue ?? 0}
                  </td>
                  <td className={styles.performanceCell}>{row.performance_summary || "—"}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DailyWorksheet;
